'use client';

import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  DollarSign,
  Wallet,
  Clock,
  Package,
  AlertTriangle,
} from 'lucide-react';

// Backend integration: GET /api/seller/dashboard/stats
const kpiData = [
  {
    id: 'kpi-today-sales',
    label: "Today\'s Sales",
    value: 'PKR 18,450',
    rawValue: 18450,
    change: +12.4,
    changeLabel: 'vs yesterday',
    icon: <TrendingUp size={20} />,
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
    trend: 'up',
    subtext: '23 orders',
    span: 'col-span-1',
  },
  {
    id: 'kpi-week-sales',
    label: 'This Week',
    value: 'PKR 94,200',
    rawValue: 94200,
    change: +8.1,
    changeLabel: 'vs last week',
    icon: <TrendingUp size={20} />,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    trend: 'up',
    subtext: '118 orders',
    span: 'col-span-1',
  },
  {
    id: 'kpi-total-earnings',
    label: 'Total Earnings',
    value: 'PKR 4,82,750',
    rawValue: 482750,
    change: +22.3,
    changeLabel: 'this month',
    icon: <DollarSign size={20} />,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    trend: 'up',
    subtext: 'After platform fees',
    span: 'col-span-2',
    featured: true,
  },
  {
    id: 'kpi-available-balance',
    label: 'Available Balance',
    value: 'PKR 24,850',
    rawValue: 24850,
    change: null,
    changeLabel: 'Withdraw anytime',
    icon: <Wallet size={20} />,
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
    trend: 'neutral',
    subtext: 'Min. PKR 500',
    span: 'col-span-1',
    actionLabel: 'Withdraw',
  },
  {
    id: 'kpi-pending-balance',
    label: 'Pending Clearance',
    value: 'PKR 38,600',
    rawValue: 38600,
    change: null,
    changeLabel: '7-day hold',
    icon: <Clock size={20} />,
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
    trend: 'warning',
    subtext: 'Clears 17 May',
    span: 'col-span-1',
  },
  {
    id: 'kpi-total-orders',
    label: 'Total Orders',
    value: '1,284',
    rawValue: 1284,
    change: -3.2,
    changeLabel: 'vs last month',
    icon: <ShoppingCart size={20} />,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    trend: 'down',
    subtext: '14 need action',
    span: 'col-span-1',
    alert: true,
  },
  {
    id: 'kpi-total-products',
    label: 'Active Products',
    value: '347',
    rawValue: 347,
    change: +5,
    changeLabel: 'added this week',
    icon: <Package size={20} />,
    iconBg: 'bg-accent/10',
    iconColor: 'text-accent',
    trend: 'up',
    subtext: '12 low stock',
    span: 'col-span-1',
  },
];

export default function KPIBentoGrid() {
  return (
    // 7 cards: grid-cols-4
    // row 1: Total Earnings spans 2 cols + Today's Sales + Week Sales = 4 cols
    // row 2: Available Balance + Pending + Total Orders + Active Products = 4 cols
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* Today's Sales */}
      <KPICard item={kpiData[0]} />
      {/* Week Sales */}
      <KPICard item={kpiData[1]} />
      {/* Total Earnings — featured hero, spans 2 cols */}
      <KPICard item={kpiData[2]} className="col-span-2" />
      {/* Available Balance */}
      <KPICard item={kpiData[3]} />
      {/* Pending Clearance */}
      <KPICard item={kpiData[4]} />
      {/* Total Orders */}
      <KPICard item={kpiData[5]} />
      {/* Active Products */}
      <KPICard item={kpiData[6]} />
    </div>
  );
}

interface KPIItem {
  id: string;
  label: string;
  value: string;
  change: number | null;
  changeLabel: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  trend: string;
  subtext: string;
  featured?: boolean;
  alert?: boolean;
  actionLabel?: string;
}

function KPICard({ item, className = '' }: { item: KPIItem; className?: string }) {
  const isUp = item.trend === 'up';
  const isDown = item.trend === 'down';
  const isWarning = item.trend === 'warning';

  const cardBg = item.alert
    ? 'bg-danger/5 border-danger/20'
    : item.featured
    ? 'bg-primary border-primary' :'bg-card border-border';

  const labelColor = item.featured ? 'text-primary-foreground/70' : 'text-muted-foreground';
  const valueColor = item.featured ? 'text-primary-foreground' : 'text-foreground';
  const subtextColor = item.featured ? 'text-primary-foreground/60' : 'text-muted-foreground';

  return (
    <div className={`card ${cardBg} p-4 hover:shadow-card-hover transition-shadow duration-200 ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className={`text-xs font-medium uppercase tracking-wide ${labelColor}`}>
            {item.label}
          </p>
        </div>
        <div className={`w-9 h-9 rounded-lg ${item.featured ? 'bg-white/20' : item.iconBg} flex items-center justify-center shrink-0`}>
          <span className={item.featured ? 'text-white' : item.iconColor}>{item.icon}</span>
        </div>
      </div>

      <p className={`text-hero-value ${valueColor} mb-1`}>{item.value}</p>

      <div className="flex items-center justify-between mt-2">
        <p className={`text-xs ${subtextColor}`}>{item.subtext}</p>
        {item.change !== null && (
          <div className={`flex items-center gap-1 text-xs font-semibold ${
            item.featured
              ? isUp ? 'text-green-300' : 'text-red-300' : isUp ?'text-success' : isDown ? 'text-danger' : 'text-muted-foreground'
          }`}>
            {isUp ? <TrendingUp size={12} /> : isDown ? <TrendingDown size={12} /> : null}
            {item.change > 0 ? '+' : ''}{item.change}%
          </div>
        )}
        {item.alert && (
          <span className="flex items-center gap-1 text-xs text-danger font-semibold">
            <AlertTriangle size={11} />
            Action needed
          </span>
        )}
        {item.actionLabel && (
          <button className="text-xs font-semibold text-primary hover:underline">
            {item.actionLabel} →
          </button>
        )}
        {isWarning && item.change === null && (
          <span className="text-xs text-warning font-medium">{item.changeLabel}</span>
        )}
      </div>
    </div>
  );
}